import { useState, useEffect, useRef } from 'react';

/**
 * NestedStatusDropdown — 2-level status picker mirroring FilterDropdown visual style.
 * Trigger shows sub_pill only (Phase 69 — D-08 amended after UAT feedback);
 * menu lists children grouped by parent. Parent context preserved through
 * Phase 50 D-05 sub-status color palette.
 *
 * Phase 52 D-13. Replaces stock <select> pair in LeadDetailsModal Info tab.
 * CSS selectors `.nested-status-dropdown__*` live in styles.css (added by Plan 01 Task 2).
 *
 * @param {string|null}  value        current sub-status id
 * @param {Array}        statuses     full status list including parents (parentId=null)
 * @param {Function}     onChange     (newSubId: string) => void
 * @param {string}       placeholder  shown when value resolves to nothing
 * @param {boolean}      disabled     optional — trigger acts as read-only
 */
export const NestedStatusDropdown = ({ value, statuses, onChange, placeholder = '—', disabled = false }) => {
    const [isOpen, setIsOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (ref.current && !ref.current.contains(event.target)) setIsOpen(false);
        };
        if (isOpen) document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    const list = Array.isArray(statuses) ? statuses : [];
    const sub = list.find(s => s.id === value) || null;
    const parents = list.filter(s => !s.parentId);

    const handlePick = (childId) => {
        if (disabled) return;
        onChange?.(childId);
        setIsOpen(false);
    };

    return (
        <div className='filter-dropdown nested-status-dropdown' ref={ref}>
            {/* Phase 69 (UAT round 2) — bare-badge trigger (D-18); kanban parity, no box wrapper */}
            <button
                type='button'
                className={`nested-status-dropdown__bare-trigger${disabled ? ' nested-status-dropdown__bare-trigger--disabled' : ''}`}
                onClick={() => { if (!disabled) setIsOpen(!isOpen); }}
                aria-haspopup='listbox'
                aria-expanded={isOpen}
            >
                {sub ? (
                    <span className={`status-badge status-badge--${sub.id}`}>{sub.label}</span>
                ) : (
                    <span style={{ opacity: 0.6 }}>{placeholder}</span>
                )}
                <span className='nested-status-dropdown__chevron' aria-hidden='true'>▾</span>
            </button>
            {isOpen && (
                <div className='filter-dropdown__menu nested-status-dropdown__menu'>
                    {parents.map(p => {
                        const pChildren = list.filter(s => s.parentId === p.id);
                        if (!pChildren.length) return null;
                        return (
                            <div key={p.id} className='nested-status-dropdown__group'>
                                <div className={`nested-status-dropdown__parent status-badge status-badge--${p.id}`}>
                                    {p.label}
                                </div>
                                {pChildren.map(c => (
                                    <button
                                        key={c.id}
                                        type='button'
                                        className={`filter-dropdown__item${c.id === value ? ' filter-dropdown__item--active' : ''}`}
                                        onClick={() => handlePick(c.id)}
                                    >
                                        <span className={`filter-dropdown__dot status-badge--${c.id}`} />
                                        {c.label}
                                    </button>
                                ))}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};
